import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Layers, Search, MapPin, AlertTriangle, Loader2, ArrowUpDown, ArrowLeft } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { useLocation } from '@/contexts/LocationContext';
import { useDebouncedValue } from '@/hooks/useDebouncedValue';
import { useBatchList, TAB_STATUSES, type BatchTab } from '../hooks/useBatchList';
import { BatchListTable } from './BatchListTable';
import { PaginationControls } from './PaginationControls';
import { trpc } from '@/lib/trpc';

function TabBadge({
  count,
  isLoading,
  variant = 'default',
}: {
  count: number | undefined;
  isLoading: boolean;
  variant?: 'default' | 'muted';
}) {
  if (isLoading) {
    return <Skeleton className="h-5 w-6 rounded-full" />;
  }

  if (count === undefined) {
    return null;
  }

  if (variant === 'muted') {
    return (
      <span className="text-muted-foreground text-xs tabular-nums">
        ({count})
      </span>
    );
  }

  if (count === 0) return null;

  return (
    <span
      className={cn(
        'text-xs px-1.5 py-0.5 rounded-full tabular-nums min-w-[1.25rem] text-center',
        'bg-primary/15 text-primary font-medium'
      )}
    >
      {count}
    </span>
  );
}

export function BatchesListPage() {
  const { t } = useTranslation('production');
  const navigate = useNavigate();
  const { selectedLocation } = useLocation();
  const locationId = selectedLocation?.id;

  const [activeTab, setActiveTab] = useState<BatchTab>('open');
  const [searchInput, setSearchInput] = useState('');
  const [sortOrder, setSortOrder] = useState<'newest' | 'oldest'>('newest');

  const debouncedSearch = useDebouncedValue(searchInput, 300);

  const {
    batches,
    isLoading,
    isFetching,
    error,
    refetch,
    currentPage,
    totalPages,
    offset,
    goToPage,
  } = useBatchList({
    locationId,
    tab: activeTab,
    search: debouncedSearch,
    sortOrder,
    pageSize: 50,
  });

  // Lightweight count queries for tab badges
  const openCountQuery = trpc.productionOrders.listBatches.useQuery(
    { locationId: locationId!, statuses: TAB_STATUSES.open, limit: 1, offset: 0 },
    { enabled: !!locationId, staleTime: 30_000 }
  );
  const completedCountQuery = trpc.productionOrders.listBatches.useQuery(
    { locationId: locationId!, statuses: TAB_STATUSES.completed, limit: 1, offset: 0 },
    { enabled: !!locationId, staleTime: 30_000 }
  );
  const allCountQuery = trpc.productionOrders.listBatches.useQuery(
    { locationId: locationId!, limit: 1, offset: 0 },
    { enabled: !!locationId, staleTime: 30_000 }
  );

  const openCount = openCountQuery.data?.pagination?.total;
  const completedCount = completedCountQuery.data?.pagination?.total;
  const allCount = allCountQuery.data?.pagination?.total;
  const countsLoading = openCountQuery.isLoading || completedCountQuery.isLoading || allCountQuery.isLoading;

  const handleBatchClick = (batchId: string) => {
    navigate(`/production/batches/${batchId}`);
  };

  if (!locationId) {
    return (
      <div className="empty-state">
        <div className="empty-state-icon">
          <MapPin className="h-6 w-6 text-muted-foreground" />
        </div>
        <p className="empty-state-title">{t('batch.noLocationSelected')}</p>
        <p className="empty-state-description">
          {t('orders.selectLocationQueue')}
        </p>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-6 py-6 max-w-7xl animate-enter">
      {/* Page Header */}
      <div className="page-header">
        <div className="flex items-center gap-3">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate('/production/picker')}
            aria-label="Back to picker"
          >
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10">
            <Layers className="h-5 w-5 text-primary" />
          </div>
          <div>
            <h1 className="page-title">{t('batches.title')}</h1>
            <p className="page-description">
              {t('batches.description', { location: selectedLocation?.name })}
            </p>
          </div>
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={(v) => setActiveTab(v as BatchTab)}>
        <TabsList className="mb-6 h-14">
          <TabsTrigger value="open" className="gap-2 px-4">
            {t('batches.open')}
            <TabBadge count={openCount} isLoading={countsLoading} variant="default" />
          </TabsTrigger>
          <TabsTrigger value="completed" className="gap-2 px-4">
            {t('batches.completed')}
            <TabBadge count={completedCount} isLoading={countsLoading} variant="muted" />
          </TabsTrigger>
          <TabsTrigger value="all" className="gap-2 px-4">
            {t('batches.all')}
            <TabBadge count={allCount} isLoading={countsLoading} variant="muted" />
          </TabsTrigger>
        </TabsList>

        {/* Search + Sort bar */}
        <div className="flex items-center gap-3 mb-4">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder={t('batches.searchPlaceholder')}
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              className="pl-9"
            />
          </div>
          <Select value={sortOrder} onValueChange={(v) => setSortOrder(v as 'newest' | 'oldest')}>
            <SelectTrigger className="w-[160px]">
              <ArrowUpDown className="w-4 h-4 mr-2" />
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="newest">{t('batches.newestFirst')}</SelectItem>
              <SelectItem value="oldest">{t('batches.oldestFirst')}</SelectItem>
            </SelectContent>
          </Select>
          {isFetching && !isLoading && (
            <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
          )}
        </div>

        {/* Error state */}
        {error && (
          <Alert variant="destructive" className="mb-4">
            <AlertTriangle className="h-4 w-4" />
            <AlertDescription className="flex items-center justify-between">
              <span>{t('batches.failedToLoad')}</span>
              <Button variant="outline" size="sm" onClick={() => refetch()}>
                {t('batches.retry')}
              </Button>
            </AlertDescription>
          </Alert>
        )}

        {/* Shared content area for all tabs */}
        <TabsContent value="open" className="mt-0">
          <BatchListContent
            batches={batches}
            isLoading={isLoading}
            isFetching={isFetching}
            currentPage={currentPage}
            totalPages={totalPages}
            offset={offset}
            searchTerm={debouncedSearch}
            onBatchClick={handleBatchClick}
            onPageChange={goToPage}
            emptyMessage={t('batches.noOpenBatches')}
            emptyDescription={t('batches.allCompleted')}
          />
        </TabsContent>

        <TabsContent value="completed" className="mt-0">
          <BatchListContent
            batches={batches}
            isLoading={isLoading}
            isFetching={isFetching}
            currentPage={currentPage}
            totalPages={totalPages}
            offset={offset}
            searchTerm={debouncedSearch}
            onBatchClick={handleBatchClick}
            onPageChange={goToPage}
            emptyMessage={t('batches.noCompletedBatches')}
            emptyDescription={t('batches.completedWillAppear')}
          />
        </TabsContent>

        <TabsContent value="all" className="mt-0">
          <BatchListContent
            batches={batches}
            isLoading={isLoading}
            isFetching={isFetching}
            currentPage={currentPage}
            totalPages={totalPages}
            offset={offset}
            searchTerm={debouncedSearch}
            onBatchClick={handleBatchClick}
            onPageChange={goToPage}
            emptyMessage={t('batches.noBatches')}
            emptyDescription={t('batches.createFromOrders')}
          />
        </TabsContent>
      </Tabs>
    </div>
  );
}

function BatchListContent({
  batches,
  isLoading,
  isFetching,
  currentPage,
  totalPages,
  offset,
  searchTerm,
  onBatchClick,
  onPageChange,
  emptyMessage,
  emptyDescription,
}: {
  batches: ReturnType<typeof useBatchList>['batches'];
  isLoading: boolean;
  isFetching: boolean;
  currentPage: number;
  totalPages: number;
  offset: number;
  searchTerm: string;
  onBatchClick: (batchId: string) => void;
  onPageChange: (page: number) => void;
  emptyMessage: string;
  emptyDescription: string;
}) {
  if (isLoading) {
    return (
      <div className="space-y-2">
        {[1, 2, 3, 4, 5].map((i) => (
          <div key={i} className="flex gap-4 py-3 border-b last:border-0">
            <Skeleton className="h-6 w-32" />
            <Skeleton className="h-6 w-8 ml-auto" />
            <Skeleton className="h-6 w-8" />
            <Skeleton className="h-6 w-6" />
            <Skeleton className="h-6 w-6" />
            <Skeleton className="h-6 w-24" />
            <Skeleton className="h-6 w-28" />
          </div>
        ))}
      </div>
    );
  }

  if (batches.length === 0 && !isFetching) {
    const message = searchTerm
      ? `No batches match "${searchTerm}"`
      : emptyMessage;
    const description = searchTerm
      ? 'Try adjusting your search or switching tabs.'
      : emptyDescription;

    return (
      <div className="empty-state">
        <div className="empty-state-icon">
          <Layers className="h-6 w-6 text-muted-foreground" />
        </div>
        <p className="empty-state-title">{message}</p>
        <p className="empty-state-description">{description}</p>
      </div>
    );
  }

  return (
    <>
      <div className={cn(isFetching && !isLoading && 'opacity-60 transition-opacity')}>
        <BatchListTable
          batches={batches}
          onBatchClick={onBatchClick}
          isLoading={isLoading}
        />
      </div>
      {totalPages > 1 && (
        <PaginationControls
          paginationMode="paginated"
          currentPage={currentPage}
          totalPages={totalPages}
          hasMore={currentPage < totalPages - 1}
          isFetching={isFetching}
          orderCount={batches.length}
          currentOffset={offset}
          ariaLabel={`Batch list pagination, page ${currentPage + 1} of ${totalPages}`}
          onPageChange={onPageChange}
          onLoadMore={() => {}}
        />
      )}
    </>
  );
}
